import clsx from 'clsx';
import React, { ReactNode } from 'react';
import styled from 'styled-components';
import colors from '../../constants/colors';
import Text, { TextProps } from '../Text/Text';
import { Row } from 'antd';

const typeStyles = `
display: inline-flex;
padding: 0px 24px;
height: 56px;
flex-direction: column;
justify-content: center;
align-items: center;
gap: 4px;
border-radius: 4px;

&.type_primary {
  background: ${colors.primary[5]};
  &:hover, &:active {
    background: ${colors.primary[6]};
  }
  &:active { opacity: 0.8; }
  &:disabled {
    opacity: 0.5;
    pointer-events: none;
  }
}


&.type_second {
  background: ${colors.secondary[4]};
  &:hover, &:active {
    background: ${colors.secondary[5]};
  }
  &:active { opacity: 0.8; }
  &:disabled {
    opacity: 0.5;
    pointer-events: none;
  }
}


&.type_light {
  background: ${colors.neutral.bg[1]};
  border: 1px solid ${colors.neutral.border[1]};
  &:hover, &:active {
    background: ${colors.neutral.bg[2]};
  }
  &:active { opacity: 0.8; }
  &:disabled {
    opacity: 0.5; 
    pointer-events: none;
  }
}

&.type_tertiary {
  background: ${colors.primary[1]};

  &:hover, &:active {
    background: ${colors.primary[2]};
  }
  &:active { opacity: 0.8; }
  &:disabled { 
    opacity: 0.5; 
    pointer-events: none;
  }
}

&.type_ghost {
  background: #FFFFFF;
  
  &:hover, &:active{
    .btn-text {
      color: ${colors.primary[6]};
    }
  }
  &:active { 
    opacity: 0.8; 
  }
  &:disabled { 
    opacity: 0.5; 
    pointer-events: none;
  }
}

&.type_cancel {
  background: ${colors.neutral.bg[3]};
  border: 1px solid ${colors.neutral.border[1]};
  &:hover, &:active {
    background: ${colors.neutral.bg[4]};
  }
  &:active { opacity: 0.8; }
  &:disabled { 
    opacity: 0.5;
    pointer-events: none;
  }
}

&.type_new {
  background: ${colors.neutral.bg[1]};
  border: 1px solid ${colors.primary[5]};
  &:hover, &:active {
    .btn-text {
      color: ${colors.primary[6]};
    }
  }
  &:active { opacity: 0.8; }
  &:disabled { 
    opacity: 0.5; 
    pointer-events: none;
  }
}

&.type_red {
  background: ${colors.other[3]};
  &:hover, &:active {
    background: ${colors.other[2]};
  }
  &:active { opacity: 0.8; }
  &:disabled { 
    opacity: 0.5; 
    pointer-events: none;
  }
}


&.type_delete {
  background: #ffffff;
  border: 1px solid ${colors.other[3]};
  &:hover, &:active {
    border-color: ${colors.other[2]};
    .btn-text {
      color: ${colors.other[2]};
    }
  }
  &:active { opacity: 0.8; }
  &:disabled { 
    opacity: 0.5;
    pointer-events: none;
  }
}

&.block {
  align-self: stretch;
}

&.sm {
  height: 32px;
}

&.md {
  height: 40px;
}

&.lg {
  height: 48px;
}
`;

const Container = styled.button`
  ${typeStyles}
`;

const PrefixSuffixWrapper = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  & > svg {
    vertical-align: middle;
    display: inline-block;
    flex-shrink: 0;
    min-width: 0;
  }
`;

type TypeProps = {
  type_primary?: boolean;
  type_second?: boolean;
  type_light?: boolean;
  type_tertiary?: boolean;
  type_ghost?: boolean;
  type_cancel?: boolean;
  type_red?: boolean;
  type_new?: boolean;
  type_delete?: boolean;
  sm?: boolean;
  md?: boolean;
  lg?: boolean;
};

const textStyles = (props: ButtonProps): TextProps => {
  const variant = props.sm ? 'body_1' : 'content_2';
  let color = 'neutral.text.1';
  if (props.type_ghost || props.type_new) {
    color = 'primary.5';
  }
  if ( props.type_delete) {
    color = 'other.3';
  }

  if (props.type_light) {
    color = 'neutral.text.3';
  }

  if (props.type_tertiary || props.type_cancel) {
    color = 'neutral.text.5';
  }
  const textProps: TextProps = {
    color: color,
    center: true,
    semibold: true,
    [variant]: true,
  } as TextProps;

  return textProps;
};
export type ButtonProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'prefix'> &
  TypeProps & {
    className?: string;
    type?: 'button' | 'submit' | 'reset';
    block?: boolean;
    prefix?: ReactNode;
    suffix?: ReactNode;
  };
const Button = ({ 
  children, 
  className, 
  type = 'button', 
  prefix, 
  suffix,
  type_primary,
  type_second,
  type_light,
  type_tertiary,
  type_ghost,
  type_cancel,
  type_new,
  type_red,
  type_delete,
  sm,
  md,
  lg,
  block,
  ...props 
}: ButtonProps) => {
  const styleProps = {
    type_primary,
    type_second,
    type_light,
    type_tertiary,
    type_ghost,
    type_cancel,
    type_new,
    type_red,
    type_delete,
    sm,
    md,
    lg,
    block,
  };

  return (
    <Container {...props} type={type} className={clsx(styleProps, className)}>
      {prefix || suffix ? (
        <Row wrap={false} style={{ gap: 8, alignItems: 'center' }}>
          {prefix && <PrefixSuffixWrapper>{prefix}</PrefixSuffixWrapper>}
          <Text className='btn-text' {...textStyles(styleProps)}>
            {children}
          </Text>
          {suffix && <PrefixSuffixWrapper>{suffix}</PrefixSuffixWrapper>}
        </Row>
      ) : (
        <Text className='btn-text' {...textStyles(styleProps)}>
          {children}
        </Text>
      )}
    </Container>
  );
};

export default Button;
